import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { getStoredPageSize, setStoredPageSize } from '@/components/Pagination';
import { supabase } from '@/lib/supabase';

// Module-level constants - never recreated
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => {
  const year = CURRENT_YEAR - 2 + i;
  return { value: year.toString(), label: year.toString() };
});

/**
 * Hook to load calendar notes page data with server-side pagination and filtering
 * Uses optimized RPC call with pre-computed display fields
 *
 * @param {string} companyId - Company ID
 * @param {Object} options - Configuration options
 * @param {number} options.pageSize - Items per page (default 25)
 * @param {number} options.debounceMs - Search debounce delay (default 300ms)
 * @returns {Object} - { notes, stats, loading, error, pagination, filters, refetch }
 */
export function useCalendarNotes(companyId, options = {}) {
  const { pageSize: initialPageSize = 25, debounceMs = 300 } = options;

  // Data state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, _setPageSize] = useState(() => getStoredPageSize(initialPageSize));
  const setPageSize = useCallback((val) => { _setPageSize(val); setStoredPageSize(val); }, []);

  // Filter state - default to all years
  const [yearFilter, setYearFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Refs for request management
  const loadingRef = useRef(false);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Debounce search term
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page on search
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, debounceMs]);

  // Reset page when year filter changes
  useEffect(() => {
    setPage(1);
  }, [yearFilter]);

  /**
   * Load calendar notes data from server
   */
  const loadData = useCallback(async () => {
    if (!companyId) {
      console.log('[useCalendarNotes] Missing companyId');
      setLoading(false);
      return null;
    }

    // Prevent concurrent requests
    if (loadingRef.current) {
      console.log('[useCalendarNotes] Already loading, skipping...');
      return null;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[useCalendarNotes] Loading data with single RPC call...');
      console.time('[useCalendarNotes] RPC call duration');

      const offset = (page - 1) * pageSize;

      // Build filter params - 'all' means no filter
      const yearParam = yearFilter && yearFilter !== 'all' ? parseInt(yearFilter, 10) : null;
      const searchParam = debouncedSearch || null;

      // Single RPC call with server-side pagination and filtering
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_calendar_notes_page',
        {
          p_company_id: companyId,
          p_year: yearParam,
          p_search: searchParam,
          p_limit: pageSize,
          p_offset: offset
        }
      );

      console.timeEnd('[useCalendarNotes] RPC call duration');

      if (rpcError) {
        console.error('[useCalendarNotes] RPC error:', rpcError);
        setError(rpcError.message);
        setData(null);
        return null;
      }

      console.log('[useCalendarNotes] RPC response:', {
        notesCount: rpcData?.notes?.length || 0,
        totalCount: rpcData?.total_count || 0,
        filteredCount: rpcData?.filtered_count || 0,
        stats: rpcData?.stats
      });

      setData(rpcData);
      return rpcData;
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return null;
      }
      console.error('[useCalendarNotes] Error:', err);
      setError(err.message);
      setData(null);
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [companyId, page, pageSize, yearFilter, debouncedSearch]);

  // Load data when dependencies change
  useEffect(() => {
    loadData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadData]);

  /**
   * Change page size and reset to first page
   */
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(1);
  }, []);

  /**
   * Set year filter
   */
  const handleYearChange = useCallback((year) => {
    setYearFilter(year);
  }, []);

  // Computed pagination values
  const totalPages = useMemo(() => {
    const filteredCount = data?.filtered_count || 0;
    return Math.ceil(filteredCount / pageSize);
  }, [data?.filtered_count, pageSize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // Data
    notes: data?.notes || [],
    stats: data?.stats || {
      total: 0,
      this_year: 0,
      upcoming: 0,
      past: 0
    },

    // Loading state
    loading,
    error,

    // Pagination
    pagination: {
      page,
      pageSize,
      totalPages,
      totalItems: data?.total_count || 0,
      filteredItems: data?.filtered_count || 0,
      setPage,
      setPageSize: handlePageSizeChange
    },

    // Filters
    filters: {
      year: yearFilter,
      search: searchTerm,
      setYear: handleYearChange,
      setSearch: setSearchTerm
    },

    // Actions
    refetch: loadData,

    // Static data (module-level constants)
    years: YEARS
  };
}

/**
 * Hook for calendar note actions (create, update, delete)
 * Separated to keep concerns clean
 *
 * @param {string} companyId - Company ID for creating notes
 * @param {string} userId - User ID for created_by field
 * @param {Function} onSuccess - Callback after successful action
 * @returns {Object} - { createNote, updateNote, deleteNote, processing }
 */
export function useCalendarNoteActions(companyId, userId, onSuccess) {
  const [processing, setProcessing] = useState(false);

  /**
   * Create a new calendar note
   */
  const createNote = useCallback(async (noteData) => {
    if (!noteData?.note_date || !noteData?.note_text?.trim()) {
      return { success: false, error: 'Date and note text are required' };
    }

    if (!companyId) {
      return { success: false, error: 'Company ID is required' };
    }

    setProcessing(true);

    try {
      const { error } = await supabase
        .from('attendance_calendar_notes')
        .insert({
          company_id: companyId,
          note_date: noteData.note_date,
          note_text: noteData.note_text.trim(),
          created_by: userId
        });

      if (error) {
        // Handle unique constraint violation
        if (error.code === '23505') {
          return { success: false, error: 'A note already exists for this date' };
        }
        throw error;
      }

      if (onSuccess) onSuccess();
      return { success: true };
    } catch (err) {
      console.error('[useCalendarNoteActions] Create error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [companyId, userId, onSuccess]);

  /**
   * Update an existing calendar note
   */
  const updateNote = useCallback(async (noteId, noteText) => {
    if (!noteId) {
      return { success: false, error: 'Note ID is required' };
    }

    if (!noteText?.trim()) {
      return { success: false, error: 'Note text cannot be empty' };
    }

    setProcessing(true);

    try {
      const { error } = await supabase
        .from('attendance_calendar_notes')
        .update({
          note_text: noteText.trim(),
          updated_date: new Date().toISOString()
        })
        .eq('id', noteId);

      if (error) throw error;

      if (onSuccess) onSuccess();
      return { success: true };
    } catch (err) {
      console.error('[useCalendarNoteActions] Update error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [onSuccess]);

  /**
   * Delete a calendar note
   */
  const deleteNote = useCallback(async (noteId) => {
    if (!noteId) {
      return { success: false, error: 'Note ID is required' };
    }

    setProcessing(true);

    try {
      const { error } = await supabase
        .from('attendance_calendar_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      if (onSuccess) onSuccess();
      return { success: true };
    } catch (err) {
      console.error('[useCalendarNoteActions] Delete error:', err);
      return { success: false, error: err.message };
    } finally {
      setProcessing(false);
    }
  }, [onSuccess]);

  return {
    createNote,
    updateNote,
    deleteNote,
    processing
  };
}
